import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { licencesApi } from '../services/api';

const PLAN_OPTIONS = ['starter', 'professional', 'enterprise'];
const STATUS_OPTIONS = ['issued', 'active', 'suspended', 'revoked', 'expired'];

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
};

export default function MasterLicencesPage() {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedLicenceId, setSelectedLicenceId] = useState('');
    const [issuedKey, setIssuedKey] = useState('');
    const [copyState, setCopyState] = useState('');
    const [distribution, setDistribution] = useState(null);
    const [downloadState, setDownloadState] = useState('');

    const [issueForm, setIssueForm] = useState({
        tenantName: '',
        plan: 'professional',
        months: 12,
        maxUsers: '',
        maxStores: '',
        maxProducts: '',
        featuresCsv: '',
    });

    const [extendForm, setExtendForm] = useState({
        licenceId: '',
        months: 1,
        plan: '',
        maxUsers: '',
        maxStores: '',
        maxProducts: '',
        featuresCsv: '',
    });

    const [reissueForm, setReissueForm] = useState({
        licenceId: '',
        months: '',
    });

    const [statusForm, setStatusForm] = useState({
        licenceId: '',
        status: 'suspended',
    });
    const [selectionNotice, setSelectionNotice] = useState('');

    const filteredRows = useMemo(() => {
        if (statusFilter === 'all') return rows;
        return rows.filter((row) => String(row.status || '').toLowerCase() === statusFilter);
    }, [rows, statusFilter]);

    const parseIntOrUndefined = (value) => {
        if (value === '' || value === null || value === undefined) return undefined;
        const num = parseInt(value, 10);
        return Number.isNaN(num) ? undefined : num;
    };

    const parseFeatures = (csv) => {
        if (!csv || !String(csv).trim()) return undefined;
        const list = String(csv)
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        return list.length > 0 ? list : undefined;
    };

    const loadLicences = useCallback(async () => {
        setLoading(true);
        const response = await licencesApi.list({ limit: 200, status: statusFilter !== 'all' ? statusFilter : undefined });
        if (!response.success) {
            window.alert(response.error || 'Could not load licences');
            setRows([]);
            setLoading(false);
            return;
        }

        const data = Array.isArray(response.data) ? response.data : [];
        setRows(data);

        const distributionResponse = await licencesApi.getDistribution();
        if (distributionResponse.success) {
            setDistribution(distributionResponse.data || null);
        }

        if (data.length > 0 && !selectedLicenceId) {
            const first = String(data[0].licence_id || '');
            setSelectedLicenceId(first);
            setExtendForm((prev) => ({ ...prev, licenceId: first }));
            setReissueForm((prev) => ({ ...prev, licenceId: first }));
            setStatusForm((prev) => ({ ...prev, licenceId: first }));
        }

        setLoading(false);
    }, [selectedLicenceId, statusFilter]);

    useEffect(() => {
        loadLicences();
    }, [loadLicences]);

    const issueLicence = async (event) => {
        event.preventDefault();
        if (!issueForm.tenantName.trim()) {
            window.alert('Tenant name is required');
            return;
        }

        const payload = {
            tenantName: issueForm.tenantName.trim(),
            plan: issueForm.plan,
            months: parseIntOrUndefined(issueForm.months) || 1,
            maxUsers: parseIntOrUndefined(issueForm.maxUsers),
            maxStores: parseIntOrUndefined(issueForm.maxStores),
            maxProducts: parseIntOrUndefined(issueForm.maxProducts),
            features: parseFeatures(issueForm.featuresCsv),
        };

        const response = await licencesApi.issue(payload);
        if (!response.success) {
            window.alert(response.error || 'Could not issue licence');
            return;
        }

        setIssuedKey(response.licenceKey || '');
        setIssueForm((prev) => ({
            ...prev,
            tenantName: '',
            months: 12,
            maxUsers: '',
            maxStores: '',
            maxProducts: '',
            featuresCsv: '',
        }));
        window.alert(response.message || 'Licence issued');
        loadLicences();
    };

    const extendLicence = async (event) => {
        event.preventDefault();
        if (!extendForm.licenceId.trim()) {
            window.alert('Licence ID is required');
            return;
        }

        const payload = {
            licenceId: extendForm.licenceId.trim(),
            months: parseIntOrUndefined(extendForm.months),
            plan: extendForm.plan || undefined,
            maxUsers: parseIntOrUndefined(extendForm.maxUsers),
            maxStores: parseIntOrUndefined(extendForm.maxStores),
            maxProducts: parseIntOrUndefined(extendForm.maxProducts),
            features: parseFeatures(extendForm.featuresCsv),
        };

        const response = await licencesApi.extend(payload);
        if (!response.success) {
            window.alert(response.error || 'Could not extend licence');
            return;
        }

        window.alert(response.message || 'Licence updated');
        loadLicences();
    };

    const reissueLicence = async (event) => {
        event.preventDefault();
        if (!reissueForm.licenceId.trim()) {
            window.alert('Licence ID is required');
            return;
        }

        const confirmed = window.confirm(
            'Regenerate this licence for a moved store server? The old licence record will be revoked and a new unbound key will be created.'
        );
        if (!confirmed) return;

        const response = await licencesApi.reissue({
            licenceId: reissueForm.licenceId.trim(),
            months: parseIntOrUndefined(reissueForm.months),
        });

        if (!response.success) {
            window.alert(response.error || 'Could not regenerate licence');
            return;
        }

        setIssuedKey(response.licenceKey || '');
        window.alert(response.message || 'Licence regenerated');
        loadLicences();
    };

    const updateStatus = async (event) => {
        event.preventDefault();
        if (!statusForm.licenceId.trim()) {
            window.alert('Licence ID is required');
            return;
        }

        const response = await licencesApi.setStatus({
            licenceId: statusForm.licenceId.trim(),
            status: statusForm.status,
        });

        if (!response.success) {
            window.alert(response.error || 'Could not update licence status');
            return;
        }

        window.alert(response.message || 'Status updated');
        loadLicences();
    };

    const selectLicence = (licenceId) => {
        const id = String(licenceId || '');
        setSelectedLicenceId(id);
        setExtendForm((prev) => ({ ...prev, licenceId: id }));
        setReissueForm((prev) => ({ ...prev, licenceId: id }));
        setStatusForm((prev) => ({ ...prev, licenceId: id }));
        setSelectionNotice(`Selected ${id}. Forms were populated below.`);

        const target = document.getElementById('licence-forms-anchor');
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const copyIssuedKey = async () => {
        if (!issuedKey) return;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(issuedKey);
            } else {
                const area = document.createElement('textarea');
                area.value = issuedKey;
                area.style.position = 'fixed';
                area.style.opacity = '0';
                document.body.appendChild(area);
                area.focus();
                area.select();
                document.execCommand('copy');
                document.body.removeChild(area);
            }
            setCopyState('Copied');
            setTimeout(() => setCopyState(''), 1600);
        } catch {
            setCopyState('Copy failed');
            setTimeout(() => setCopyState(''), 1600);
        }
    };

    const downloadAsset = async (type) => {
        setDownloadState(type);

        let response;
        if (type === 'package') {
            response = await licencesApi.downloadLatestPackage();
        } else {
            response = await licencesApi.downloadInstaller(type);
        }

        if (!response.success) {
            window.alert(response.error || 'Could not download file');
        }

        setDownloadState('');
    };

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Master Licence Management</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> Master Licences</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={loadLicences}>↻</button>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="panel stock-panel">
                    <h3>Issue New Licence</h3>
                    <form onSubmit={issueLicence} className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(160px, 1fr))' }}>
                        <label>
                            <span>Tenant Name</span>
                            <input value={issueForm.tenantName} onChange={(e) => setIssueForm((prev) => ({ ...prev, tenantName: e.target.value }))} />
                        </label>
                        <label>
                            <span>Plan</span>
                            <select value={issueForm.plan} onChange={(e) => setIssueForm((prev) => ({ ...prev, plan: e.target.value }))}>
                                {PLAN_OPTIONS.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Months</span>
                            <input type="number" min="1" value={issueForm.months} onChange={(e) => setIssueForm((prev) => ({ ...prev, months: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Users</span>
                            <input type="number" min="1" value={issueForm.maxUsers} onChange={(e) => setIssueForm((prev) => ({ ...prev, maxUsers: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Stores</span>
                            <input type="number" min="1" value={issueForm.maxStores} onChange={(e) => setIssueForm((prev) => ({ ...prev, maxStores: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Products</span>
                            <input type="number" min="1" value={issueForm.maxProducts} onChange={(e) => setIssueForm((prev) => ({ ...prev, maxProducts: e.target.value }))} />
                        </label>
                        <label style={{ gridColumn: '1 / span 2' }}>
                            <span>Features (comma separated)</span>
                            <input value={issueForm.featuresCsv} onChange={(e) => setIssueForm((prev) => ({ ...prev, featuresCsv: e.target.value }))} placeholder="pos,inventory,reports" />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button className="btn" type="submit">Issue Licence</button>
                        </div>
                    </form>

                    {issuedKey ? (
                        <div style={{ marginTop: 12 }}>
                            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <p className="muted-sm" style={{ margin: 0 }}>Issued Licence Key</p>
                                <button className="btn btn-outline btn-sm" type="button" onClick={copyIssuedKey} title="Copy licence key">
                                    {copyState || 'Copy'}
                                </button>
                            </div>
                            <textarea readOnly value={issuedKey} rows={6} style={{ width: '100%' }} />
                        </div>
                    ) : null}
                </div>

                <div className="panel stock-panel">
                    <h3>Store Distribution</h3>
                    <div className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))' }}>
                        <label>
                            <span>Master Admin Portal</span>
                            <input readOnly value={distribution?.adminPortalUrl || 'https://pos.royaltech.com.ng/admin/master-licences'} />
                        </label>
                        <label>
                            <span>Store Frontend Base URL</span>
                            <input readOnly value={distribution?.storeFrontendBaseUrl || 'https://pos.royaltech.com.ng/store'} />
                        </label>
                        <label>
                            <span>Latest Store Package</span>
                            <input readOnly value={distribution?.latestPackageName || 'No package found in release'} />
                        </label>
                    </div>

                    <div className="products-toolbar-actions" style={{ marginTop: 12, flexWrap: 'wrap' }}>
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => downloadAsset('linux')} disabled={downloadState !== ''}>
                            {downloadState === 'linux' ? 'Downloading...' : 'Download Linux Installer'}
                        </button>
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => downloadAsset('windows')} disabled={downloadState !== ''}>
                            {downloadState === 'windows' ? 'Downloading...' : 'Download Windows Installer'}
                        </button>
                        <button
                            className="btn btn-sm"
                            type="button"
                            onClick={() => downloadAsset('package')}
                            disabled={downloadState !== '' || !distribution?.latestPackageUrl}
                        >
                            {downloadState === 'package' ? 'Downloading...' : 'Download Latest Store Package'}
                        </button>
                    </div>

                    {distribution?.latestPackageSize ? (
                        <p className="muted-sm" style={{ marginTop: 12, marginBottom: 0 }}>
                            Package size: {(distribution.latestPackageSize / (1024 * 1024)).toFixed(2)} MB
                        </p>
                    ) : null}
                </div>

                <div className="panel stock-panel">
                    <h3>Renew / Extend Subscription</h3>
                    <div id="licence-forms-anchor" />
                    {selectionNotice ? (
                        <p className="muted-sm" style={{ marginTop: 0 }}>{selectionNotice}</p>
                    ) : null}
                    <form onSubmit={extendLicence} className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(160px, 1fr))' }}>
                        <label>
                            <span>Licence ID</span>
                            <input value={extendForm.licenceId} onChange={(e) => setExtendForm((prev) => ({ ...prev, licenceId: e.target.value }))} />
                        </label>
                        <label>
                            <span>Add Months</span>
                            <input type="number" min="1" value={extendForm.months} onChange={(e) => setExtendForm((prev) => ({ ...prev, months: e.target.value }))} />
                        </label>
                        <label>
                            <span>Plan (optional)</span>
                            <select value={extendForm.plan} onChange={(e) => setExtendForm((prev) => ({ ...prev, plan: e.target.value }))}>
                                <option value="">No change</option>
                                {PLAN_OPTIONS.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Max Users</span>
                            <input type="number" min="1" value={extendForm.maxUsers} onChange={(e) => setExtendForm((prev) => ({ ...prev, maxUsers: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Stores</span>
                            <input type="number" min="1" value={extendForm.maxStores} onChange={(e) => setExtendForm((prev) => ({ ...prev, maxStores: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Products</span>
                            <input type="number" min="1" value={extendForm.maxProducts} onChange={(e) => setExtendForm((prev) => ({ ...prev, maxProducts: e.target.value }))} />
                        </label>
                        <label style={{ gridColumn: '1 / span 2' }}>
                            <span>Features (comma separated)</span>
                            <input value={extendForm.featuresCsv} onChange={(e) => setExtendForm((prev) => ({ ...prev, featuresCsv: e.target.value }))} />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button className="btn" type="submit">Apply Renewal</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <h3>Update Licence Status</h3>
                    <form onSubmit={updateStatus} className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))' }}>
                        <label>
                            <span>Licence ID</span>
                            <input value={statusForm.licenceId} onChange={(e) => setStatusForm((prev) => ({ ...prev, licenceId: e.target.value }))} />
                        </label>
                        <label>
                            <span>Status</span>
                            <select value={statusForm.status} onChange={(e) => setStatusForm((prev) => ({ ...prev, status: e.target.value }))}>
                                {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </label>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button className="btn btn-outline" type="submit">Save Status</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <h3>Regenerate Licence for Moved Store</h3>
                    <form onSubmit={reissueLicence} className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))' }}>
                        <label>
                            <span>Licence ID</span>
                            <input value={reissueForm.licenceId} onChange={(e) => setReissueForm((prev) => ({ ...prev, licenceId: e.target.value }))} />
                        </label>
                        <label>
                            <span>Months (blank keeps remaining)</span>
                            <input type="number" min="1" value={reissueForm.months} onChange={(e) => setReissueForm((prev) => ({ ...prev, months: e.target.value }))} />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button className="btn" type="submit">Regenerate Key</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3>Issued / Activated Licences</h3>
                        <label style={{ maxWidth: 220 }}>
                            <span>Status Filter</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">All</option>
                                {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </label>
                    </div>

                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Licence ID</th>
                                    <th>Tenant</th>
                                    <th>Plan</th>
                                    <th>Status</th>
                                    <th>Expires</th>
                                    <th>Last Validated</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={7} className="dash-empty-row">Loading...</td></tr>
                                ) : filteredRows.length === 0 ? (
                                    <tr><td colSpan={7} className="dash-empty-row">No licences found</td></tr>
                                ) : filteredRows.map((row) => (
                                    <tr key={row.id || row.licence_id} style={{ background: selectedLicenceId === row.licence_id ? '#fff7ed' : undefined }}>
                                        <td>{row.licence_id}</td>
                                        <td>{row.tenant_name}</td>
                                        <td>{row.plan}</td>
                                        <td>{row.status}</td>
                                        <td>{formatDate(row.expires_at)}</td>
                                        <td>{formatDate(row.last_validated_at)}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                <button className="btn btn-outline btn-sm" type="button" onClick={() => selectLicence(row.licence_id)}>
                                                    Use in Forms
                                                </button>
                                                <button
                                                    className="btn btn-sm"
                                                    type="button"
                                                    onClick={() => {
                                                        selectLicence(row.licence_id);
                                                        setReissueForm((prev) => ({ ...prev, licenceId: row.licence_id }));
                                                    }}
                                                >
                                                    Regenerate
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}
